/*
 * Die Zeiterfassung am Fall. Wie bei den übrigen Labelwin-Seiten liegen die Typen lokal, weil der
 * erzeugte Client die Endpunkte noch nicht kennt.
 */

/** Wo eine Sitzung im Ablauf steht — dieselben Werte wie am Server. */
export enum SupportSessionState {
    Running = 0,
    Stopped = 1,
    Closed = 2,
    Discarded = 3,
}

/** Wohin eine Sitzung am Ende geht. */
export enum SupportSessionOutcome {
    /** Nur interne Notiz — nichts nach Labelwin. */
    Note = 0,
    /** Eine Labelwin-Aufgabe, ohne Buchung (Zeit hängt am Auftrag). */
    Task = 1,
    /** Ein Kundendienst-Auftrag samt Zeitbuchung. */
    Order = 2,
}

/** Eine Support-Sitzung. */
export interface SupportSession {
    id: string;
    createdBy: string | null;
    startedAt: string;
    stoppedAt: string | null;
    finishedAt: string | null;
    state: SupportSessionState;
    /** Gemessene Dauer in Sekunden — bei laufender Uhr bis zum Zeitpunkt des Abrufs. */
    measuredSeconds: number;
    callerName: string | null;
    callerPhone: string | null;
    callerMail: string | null;
    customerId: number | null;
    customerName: string | null;
    contactId: number | null;
    supportId: string | null;
    subject: string | null;
    notes: string | null;
    callbackRequestId: string | null;
    labelTaskId: number | null;
    orderNumber: string | null;
    timeEntryId: number | null;
    bookedHours: number | null;
}

/** Antwort des Abschlusses. */
export interface FinishSupportSessionResponse {
    session: SupportSession;
    message: string;
}

/**
 * Die vergangene Zeit einer Sitzung in Sekunden.
 *
 * Bei laufender Uhr wird ab dem Startzeitpunkt weitergezählt, statt den Serverwert einzufrieren —
 * sonst stünde die Anzeige zwischen zwei Abrufen still.
 */
export const elapsedSeconds = (session: SupportSession): number =>
    session.state === SupportSessionState.Running
        ? Math.max(0, (Date.now() - Date.parse(session.startedAt)) / 1000)
        : session.measuredSeconds;

/** Sekunden als `H:MM:SS` bzw. `MM:SS`. */
export const formatDuration = (seconds: number): string => {
    const total = Math.floor(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const rest = total % 60;
    const pad = (value: number): string => String(value).padStart(2, '0');

    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(rest)}` : `${pad(minutes)}:${pad(rest)}`;
};

/** Die gemessene Dauer als Stunden mit zwei Stellen — die Vorbelegung für die Buchung. */
export const measuredHours = (session: SupportSession): number =>
    Math.round((elapsedSeconds(session) / 3600) * 100) / 100;
